import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { handleError, isResponseError } from '@/utils/errorUtils';

import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { SubmitError } from '@/components/submitError/SubmitError';

import { useChangePassword } from '@/mutations/profile';
import { tokenUtils } from '@/tokenUtils';

import { styles } from './ChangePassword.styles';
import { ChangePasswordForm } from './ChangePasswordForm';
import { TChangePasswordFormData } from './types';

type TChangePasswordProps = TModalsContext & {
  onSuccess: () => void;
};

export const ChangePassword = ({ close, onSuccess }: TChangePasswordProps) => {
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  // Save W15.
  const { mutateAsync: saveNewPassword } = useChangePassword();

  const closeModal = () => close('settings.changePassword');

  const onSubmit = async ({ newPassword, currentPassword }: TChangePasswordFormData) => {
    try {
      const token = tokenUtils.getLocallyStoredToken();
      await saveNewPassword({ token, newPassword, oldPassword: currentPassword });
      onSuccess();
      closeModal();
    } catch (err) {
      if (isResponseError(err, 400, 'ID_003')) {
        setError(t('changePassword.invalidPassword'));
      } else if (isResponseError(err, 400, 'ID_005')) {
        setError(t('forgotPassword.cantMatchCurrent'));
      } else {
        setError(`${t('errors.unknown')}\n${t('errors.tryAgain')}`);
        handleError(err, 'UNHANDLED_RESPONSE');
      }
    }
  };

  return (
    <GenericModal sx={styles.modal}>
      <GenericModalClose onClick={closeModal} />
      <SubmitError
        error={error}
        dismiss={() => setError(null)}
        sx={{ top: 0, padding: 1, marginTop: 4 }}
      />

      <ChangePasswordForm handleSubmit={onSubmit} closeModal={closeModal} />
    </GenericModal>
  );
};
